import { useState } from "react";
import { toast } from "sonner";
import { HardHat, Link2Off, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors";
const VAR_TYPES = [["text", "Texto"], ["integer", "Entero"], ["decimal", "Decimal"], ["select", "Lista"], ["date", "Fecha"], ["boolean", "Sí/No"], ["percentage", "Porcentaje"], ["currency", "Moneda"]];

export default function SetupTab({ plan, onChange }) {
  const [p, setP] = useState({
    work_unit_name: plan.work_unit_name || "Unidad", label_field: plan.label_field || "",
    variables: plan.variables || [], activities: plan.activities || [],
    resource_types: plan.resource_types || [], productivity_rules: plan.productivity_rules || [],
    calendar_exceptions: plan.calendar_exceptions || [], buffer_days: plan.buffer_days || 0,
  });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const syncCuadrillas = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/sync-cuadrillas`);
      toast.success(`${data.vinculadas} cuadrilla(s) vinculadas · ${data.productividades} productividad(es)`);
      if (data.sin_actividad?.length) toast.info(`${data.sin_actividad.length} capacidad(es) sin actividad correspondiente en el plan`);
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al vincular cuadrillas"); }
    finally { setSyncing(false); }
  };

  const unlinkCuadrillas = async () => {
    try { await api.patch(`/planning/plans/${plan.id}`, { use_cuadrillas: false }); toast.success("Cuadrillas desvinculadas"); onChange(); }
    catch { toast.error("Error"); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.patch(`/planning/plans/${plan.id}`, p); toast.success("Configuración guardada"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar"); }
    finally { setBusy(false); }
  };

  const upd = (key, i, field, value) => {
    const list = [...p[key]];
    list[i] = { ...list[i], [field]: value };
    set(key, list);
  };
  const rm = (key, i) => set(key, p[key].filter((_, j) => j !== i));

  return (
    <div className="space-y-5" data-testid="setup-tab">
      <div className="rounded-xl border border-violet/25 bg-violet/[0.03] p-5" data-testid="cuadrillas-link-card">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="overline text-navy/40 flex items-center gap-1.5"><HardHat className="w-3.5 h-3.5 text-violet" /> OMNIA Cuadrillas</div>
            <p className="text-sm text-navy/60 mt-1">
              {plan.use_cuadrillas
                ? "Vinculado: el plan usa tus cuadrillas reales — capacidad por tipo de trabajo × efectividad, y solo asigna las que están disponibles. Se re-sincroniza en cada replanificación."
                : "Usa tus cuadrillas reales como recursos del plan: capacidad por tipo de trabajo/poste, efectividad y estado (solo se asignan las disponibles)."}
            </p>
            {plan.cuadrillas_sync && plan.use_cuadrillas && (
              <div className="text-xs text-navy/50 mt-2 space-y-0.5">
                <div>{(plan.cuadrillas_sync.vinculadas || []).length} vinculada(s): {(plan.cuadrillas_sync.vinculadas || []).map((c) => c.nombre).join(", ")}</div>
                {(plan.cuadrillas_sync.excluidas || []).length > 0 && (
                  <div className="text-amber-700">Excluidas: {plan.cuadrillas_sync.excluidas.map((c) => `${c.nombre} (${c.estado})`).join(", ")}</div>
                )}
                {(plan.cuadrillas_sync.sin_actividad || []).length > 0 && (
                  <div className="text-amber-700">Capacidades sin actividad en el plan: {plan.cuadrillas_sync.sin_actividad.slice(0, 5).join("; ")}</div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={syncCuadrillas} disabled={syncing} data-testid="sync-cuadrillas-btn"
              className="rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HardHat className="w-3.5 h-3.5" />}
              {plan.use_cuadrillas ? "Re-sincronizar" : "Usar mis cuadrillas"}
            </button>
            {plan.use_cuadrillas && (
              <button onClick={unlinkCuadrillas} data-testid="unlink-cuadrillas-btn"
                className="rounded-full border border-navy/15 text-xs px-3 py-2 hover:border-navy/40 inline-flex items-center gap-1.5">
                <Link2Off className="w-3.5 h-3.5" /> Desvincular
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-navy/10 bg-white p-5 grid sm:grid-cols-3 gap-3">
        <label className="text-[10px] text-navy/40">Nombre de la unidad de trabajo
          <input className={inputCls} value={p.work_unit_name} data-testid="wu-name"
            onChange={(e) => set("work_unit_name", e.target.value)} /></label>
        <label className="text-[10px] text-navy/40">Variable identificadora (etiqueta)
          <select className={inputCls} value={p.label_field} onChange={(e) => set("label_field", e.target.value)}>
            {p.variables.map((v) => <option key={v.code} value={v.code}>{v.name}</option>)}
          </select></label>
        <label className="text-[10px] text-navy/40">Buffer (días hábiles antes del cierre)
          <input className={inputCls} type="number" min={0} value={p.buffer_days} data-testid="buffer-days"
            onChange={(e) => set("buffer_days", Number(e.target.value))} /></label>
      </div>

      <Section title="Variables" hint="Define los campos de cada unidad de trabajo"
        onAdd={() => set("variables", [...p.variables, { code: `var_${p.variables.length + 1}`, name: "", type: "text", options: [] }])} testid="add-variable">
        {p.variables.map((v, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_130px_1fr_32px] gap-2 items-center">
            <input className={inputCls} placeholder="Código" value={v.code} onChange={(e) => upd("variables", i, "code", e.target.value.toLowerCase().replaceAll(" ", "_"))} />
            <input className={inputCls} placeholder="Nombre" value={v.name} onChange={(e) => upd("variables", i, "name", e.target.value)} />
            <select className={inputCls} value={v.type} onChange={(e) => upd("variables", i, "type", e.target.value)}>
              {VAR_TYPES.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
            </select>
            <input className={inputCls} placeholder="Opciones (coma)" value={(v.options || []).join(",")} disabled={v.type !== "select"}
              onChange={(e) => upd("variables", i, "options", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
            <button onClick={() => rm("variables", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </Section>

      <Section title="Actividades" hint="Qué se ejecuta sobre cada unidad; define dependencias y cuál es la auditoría"
        onAdd={() => set("activities", [...p.activities, { code: `act_${p.activities.length + 1}`, name: "", resource_type: "", qty_field: "", requires: [], audit: false }])} testid="add-activity">
        {p.activities.map((a, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.2fr_1fr_1fr_1.2fr_60px_32px] gap-2 items-center">
            <input className={inputCls} placeholder="Código" value={a.code} onChange={(e) => upd("activities", i, "code", e.target.value.toLowerCase().replaceAll(" ", "_"))} />
            <input className={inputCls} placeholder="Nombre" value={a.name} onChange={(e) => upd("activities", i, "name", e.target.value)} />
            <select className={inputCls} value={a.resource_type} onChange={(e) => upd("activities", i, "resource_type", e.target.value)}>
              <option value="">Recurso…</option>
              {p.resource_types.map((r, i2) => <option key={`${r.code}-${i2}`} value={r.code}>{r.name}</option>)}
            </select>
            <select className={inputCls} value={a.qty_field} onChange={(e) => upd("activities", i, "qty_field", e.target.value)}>
              <option value="">Var. productividad…</option>
              {p.variables.filter((v) => ["integer", "decimal"].includes(v.type)).map((v) => <option key={v.code} value={v.code}>{v.name}</option>)}
            </select>
            <select className={inputCls} multiple size={1} value={a.requires || []} title="Depende de (Ctrl+clic para varias)"
              onChange={(e) => upd("activities", i, "requires", Array.from(e.target.selectedOptions).map((o) => o.value))}>
              {p.activities.filter((x) => x.code !== a.code).map((x) => <option key={x.code} value={x.code}>{x.name || x.code}</option>)}
            </select>
            <label className="text-xs text-navy/50 flex items-center gap-1"><input type="checkbox" checked={!!a.audit}
              onChange={(e) => upd("activities", i, "audit", e.target.checked)} /> Audit</label>
            <button onClick={() => rm("activities", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <p className="text-[11px] text-navy/35">Dependencias: mantén Ctrl/Cmd para seleccionar varias. "Audit" marca la actividad que libera la unidad tras auditarse.</p>
      </Section>

      <Section title="Recursos" hint="Tipos de recurso y cuántas instancias existen (cuadrillas, camiones, auditores…)"
        onAdd={() => set("resource_types", [...p.resource_types, { code: `rec_${p.resource_types.length + 1}`, name: "", count: 1, name_pattern: "" }])} testid="add-resource">
        {p.resource_types.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.4fr_90px_1fr_32px] gap-2 items-center">
            <input className={inputCls} placeholder="Código" value={r.code} onChange={(e) => upd("resource_types", i, "code", e.target.value.toLowerCase().replaceAll(" ", "_"))} />
            <input className={inputCls} placeholder="Nombre" value={r.name} onChange={(e) => upd("resource_types", i, "name", e.target.value)} />
            <input className={inputCls} type="number" min={1} value={r.count} onChange={(e) => upd("resource_types", i, "count", Number(e.target.value))} />
            <input className={inputCls} placeholder="Patrón nombre (Camión {n})" value={r.name_pattern || ""} onChange={(e) => upd("resource_types", i, "name_pattern", e.target.value)} />
            <button onClick={() => rm("resource_types", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </Section>

      <Section title="Productividades" hint="Capacidad por recurso y actividad; condición opcional por variable (p. ej. Zona = Provincia)"
        onAdd={() => set("productivity_rules", [...p.productivity_rules, { resource_type: "", activity: "", field: "", value: "", capacity: 1, unit: "" }])} testid="add-productivity">
        {p.productivity_rules.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_90px_1fr_32px] gap-2 items-center">
            <select className={inputCls} value={r.resource_type} onChange={(e) => upd("productivity_rules", i, "resource_type", e.target.value)}>
              <option value="">Recurso…</option>
              {p.resource_types.map((x, i2) => <option key={`${x.code}-${i2}`} value={x.code}>{x.name}</option>)}
            </select>
            <select className={inputCls} value={r.activity} onChange={(e) => upd("productivity_rules", i, "activity", e.target.value)}>
              <option value="">Actividad…</option>
              {p.activities.map((x) => <option key={x.code} value={x.code}>{x.name || x.code}</option>)}
            </select>
            <select className={inputCls} value={r.field} onChange={(e) => upd("productivity_rules", i, "field", e.target.value)}>
              <option value="">Sin condición</option>
              {p.variables.map((v) => <option key={v.code} value={v.code}>{v.name}</option>)}
            </select>
            <input className={inputCls} placeholder="Valor" value={r.value} disabled={!r.field}
              onChange={(e) => upd("productivity_rules", i, "value", e.target.value)} />
            <input className={inputCls} type="number" step="0.1" value={r.capacity}
              onChange={(e) => upd("productivity_rules", i, "capacity", Number(e.target.value))} />
            <input className={inputCls} placeholder="Unidad (postes/día)" value={r.unit || ""}
              onChange={(e) => upd("productivity_rules", i, "unit", e.target.value)} />
            <button onClick={() => rm("productivity_rules", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </Section>

      <Section title="Excepciones de calendario" hint="Días extraordinarios de trabajo o no laborables adicionales"
        onAdd={() => set("calendar_exceptions", [...p.calendar_exceptions, { fecha: "", tipo: "laborable" }])} testid="add-exception">
        {p.calendar_exceptions.map((e2, i) => (
          <div key={i} className="grid grid-cols-[200px_1fr_32px] gap-2 items-center">
            <input className={inputCls} type="date" value={e2.fecha} onChange={(ev) => upd("calendar_exceptions", i, "fecha", ev.target.value)} />
            <select className={inputCls} value={e2.tipo} onChange={(ev) => upd("calendar_exceptions", i, "tipo", ev.target.value)}>
              <option value="laborable">Día extraordinario de trabajo</option>
              <option value="no_laborable">Día no laborable adicional</option>
            </select>
            <button onClick={() => rm("calendar_exceptions", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </Section>

      <button onClick={save} disabled={busy} data-testid="setup-save"
        className="rounded-full bg-gradient-violet text-white text-sm px-6 py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar configuración
      </button>
    </div>
  );
}

function Section({ title, hint, onAdd, children, testid }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-5 space-y-2.5">
      <div className="flex items-center gap-3">
        <div>
          <div className="overline text-navy/40">{title}</div>
          <p className="text-[11px] text-navy/35">{hint}</p>
        </div>
        <button onClick={onAdd} data-testid={testid}
          className="ml-auto rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Agregar
        </button>
      </div>
      {children}
    </div>
  );
}
